import { apiRequest } from "../lib/api";
import type { Configuration } from "../lib/api-types";
import { paths } from "../lib/endpoints";
import type {
  ActorKind,
  AdminInvite,
  AdminInviteRequest,
  AdminMember,
  AdminOperator,
  AdminWorkspace,
  AuditPage,
  ErrorEntry,
  OperatorSignInLink,
  ValidationResult,
  WorkspaceStatus,
} from "./types";

const enc = encodeURIComponent;
const BASE = "/api/admin";
const ERROR_PAGE_LIMIT = 50;
const AUDIT_PAGE_LIMIT = 50;

const workspacePath = (id: string) => `${BASE}/workspaces/${enc(id)}`;
const operatorPath = (id: string) => `${BASE}/operators/${enc(id)}`;

/** Builds a query string, dropping empty values. Returns "" when nothing is set. */
export function buildQuery(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

export interface WorkspaceFilters {
  status?: WorkspaceStatus | undefined;
  q?: string | undefined;
}

export interface WorkspacePatch {
  name?: string;
  plan?: string;
  quota_bytes?: number;
  quota_persons?: number;
}

export interface AuditFilters {
  workspaceId?: string | undefined;
  actorKind?: ActorKind | undefined;
  action?: string | undefined;
  before?: string | undefined;
}

export type LifecycleAction = "suspend" | "unsuspend" | "restore" | "delete";

const LIFECYCLE: Record<LifecycleAction, (id: string) => Promise<unknown>> = {
  suspend: (id) => apiRequest(`${workspacePath(id)}/suspend`, { method: "POST" }),
  unsuspend: (id) => apiRequest(`${workspacePath(id)}/unsuspend`, { method: "POST" }),
  restore: (id) => apiRequest(`${workspacePath(id)}/restore`, { method: "POST" }),
  delete: (id) => apiRequest(workspacePath(id), { method: "DELETE" }),
};

export const adminApi = {
  listWorkspaces: (filters: WorkspaceFilters) =>
    apiRequest<AdminWorkspace[]>(`${BASE}/workspaces${buildQuery(filters)}`),

  getWorkspace: (id: string) => apiRequest<AdminWorkspace>(workspacePath(id)),

  updateWorkspace: (id: string, patch: WorkspacePatch) =>
    apiRequest<AdminWorkspace>(workspacePath(id), { method: "PATCH", body: patch }),

  runLifecycle: (id: string, action: LifecycleAction) => LIFECYCLE[action](id),

  transferOwnership: (id: string, operatorId: string) =>
    apiRequest<unknown>(`${workspacePath(id)}/transfer-ownership`, {
      method: "POST",
      body: { operatorId },
    }),

  workspaceMembers: (id: string) => apiRequest<AdminMember[]>(`${workspacePath(id)}/members`),

  workspaceConfigurations: (id: string) =>
    apiRequest<Configuration[]>(`${workspacePath(id)}/configurations`),

  startActingAs: (workspaceId: string) =>
    apiRequest<{ workspaceId: string; acting: boolean }>(paths.actAs, {
      method: "POST",
      body: { workspaceId },
    }),

  stopActingAs: () => apiRequest<{ acting: boolean }>(paths.actAs, { method: "DELETE" }),

  createInvite: (email: string, workspaceName: string | undefined) =>
    apiRequest<{ url: string }>(`${BASE}/invites`, {
      method: "POST",
      body: workspaceName ? { email, workspaceName } : { email },
    }),

  listInvites: () => apiRequest<AdminInvite[]>(`${BASE}/invites`),

  listInviteRequests: (status: "new" | "all") =>
    apiRequest<AdminInviteRequest[]>(`${BASE}/invite-requests${buildQuery({ status })}`),

  dismissInviteRequest: (id: string) =>
    apiRequest<AdminInviteRequest>(`${BASE}/invite-requests/${enc(id)}/dismiss`, {
      method: "POST",
    }),

  markInviteRequestInvited: (id: string) =>
    apiRequest<AdminInviteRequest>(`${BASE}/invite-requests/${enc(id)}/mark-invited`, {
      method: "POST",
    }),

  resendInvite: (id: string) =>
    apiRequest<unknown>(`${BASE}/invites/${enc(id)}/resend`, { method: "POST" }),

  regenerateInvite: (id: string) =>
    apiRequest<{ url: string | null; linkUnavailable: boolean }>(
      `${BASE}/invites/${enc(id)}/regenerate`,
      { method: "POST" },
    ),

  revokeInvite: (id: string) =>
    apiRequest<{ revoked: boolean }>(`${BASE}/invites/${enc(id)}`, { method: "DELETE" }),

  listOperators: (q: string) =>
    apiRequest<AdminOperator[]>(`${BASE}/operators${buildQuery({ q })}`),

  updateOperatorEmail: (id: string, email: string) =>
    apiRequest<unknown>(operatorPath(id), { method: "PATCH", body: { email } }),

  unlinkGoogle: (id: string) =>
    apiRequest<unknown>(`${operatorPath(id)}/identities/google`, { method: "DELETE" }),

  createSignInLink: (id: string) =>
    apiRequest<OperatorSignInLink>(`${operatorPath(id)}/sign-in-invite`, { method: "POST" }),

  listAudit: (filters: AuditFilters) =>
    apiRequest<AuditPage>(`${BASE}/audit${buildQuery({ ...filters, limit: AUDIT_PAGE_LIMIT })}`),

  listErrors: (workspaceId: string | undefined) =>
    apiRequest<ErrorEntry[]>(
      `${BASE}/errors${buildQuery({ workspaceId, limit: ERROR_PAGE_LIMIT })}`,
    ),

  takedownMedia: (mediaId: string, reason: string) =>
    apiRequest<unknown>(`${BASE}/media/${enc(mediaId)}/takedown`, {
      method: "POST",
      body: { reason },
    }),

  validateConfiguration: (workspaceId: string, configurationId: string) =>
    apiRequest<ValidationResult>(
      `${workspacePath(workspaceId)}/configurations/${enc(configurationId)}/validate`,
    ),
};
